import { Form, Button } from "semantic-ui-react";
import { useFormik } from "formik";
import { useState, useEffect } from "react";
import { Room, User, Level, Grade, Subject } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./LiveClassForm.form";
import { withEmptyOption } from "../../../../utils";

const liveClassController = new Room();
const userController = new User();
const levelController = new Level();
const gradeController = new Grade();
const subjectController = new Subject();

export function LiveClassForm(props) {
  const { onClose, onReload, liveClass } = props;
  const { accessToken } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [levels, setLevels] = useState([]);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [grades, setGrades] = useState([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);

  const formik = useFormik({
    initialValues: initialValues(liveClass),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const data = {
          name: formValue.name,
            description: formValue.description,
            scheduledAt: formValue.scheduledAt,
            durationMinutes: formValue.durationMinutes,
            levelId: formValue.levelId,
            gradeId: formValue.gradeId,
            subjectId: formValue.subjectId,
            teacherId: formValue.teacherId,
            accessCode: formValue.accessCode || null,
        };

        if (liveClass) {
          await liveClassController.updateRoom(
            accessToken,
            liveClass.id,
            data
          );
        } else {
          data.status = "scheduled";
          await liveClassController.createRoom(accessToken, data);
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await userController.getTeachers();
        const teacherOptions = response.map((teacher) => ({
          key: teacher.id,
          text: `${teacher.firstname} ${teacher.lastname} ${teacher.secondLastname}`,
          value: teacher.id,
        }));
        setTeachers(teacherOptions);
      } catch (error) {
        console.error("Error al cargar profesores:", error);
      } finally {
        setLoadingTeachers(false);
      }
    };

    fetchTeachers();
  }, [accessToken]);

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const response = await levelController.getLevels(accessToken);
        const levelOptions = response.map((level) => ({
          key: level.id,
          text: level.name,
          value: level.id,
        }));
        setLevels(levelOptions);
      } catch (error) {
        console.error("Error al cargar niveles:", error);
      } finally {
        setLoadingLevels(false);
      }
    };

    fetchLevels();
  }, [accessToken]);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await subjectController.getSubjects(accessToken);
        const subjectOptions = response.map((subject) => ({
          key: subject.id,
          text: subject.name,
          value: subject.id,
        }));
        setSubjects(subjectOptions);
      } catch (error) {
        console.error("Error al cargar asignaturas:", error);
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [accessToken]);

  // Cargar grados iniciales si hay un levelId al montar (modo edición)
  useEffect(() => {
    const loadInitialGrades = async () => {
      const initialLevelId = liveClass?.syllabuSubjectGrade?.grade?.levelId || liveClass?.levelId;
      if (initialLevelId) {
        setLoadingGrades(true);
        try {
          const response = await gradeController.getGradesByLevelId(
            initialLevelId,
            accessToken
          );
          const gradeOptions = response.map((grade) => ({
            key: grade.id,
            text: grade.name,
            value: grade.id,
          }));
          setGrades(gradeOptions);
        } catch (error) {
          console.error("Error al cargar grados iniciales:", error);
        } finally {
          setLoadingGrades(false);
        }
      }
    };

    loadInitialGrades();
  }, []);

  useEffect(() => {
    const fetchGrades = async () => {
      if (!formik.values.levelId) {
        setGrades([]);
        formik.setFieldValue("gradeId", "");
        return;
      }

      setLoadingGrades(true);
      try {
        const response = await gradeController.getGradesByLevelId(
          formik.values.levelId,
          accessToken
        );
        const gradeOptions = response.map((grade) => ({
          key: grade.id,
          text: grade.name,
          value: grade.id,
        }));
        setGrades(gradeOptions);
      } catch (error) {
        console.error("Error al cargar grados:", error);
      } finally {
        setLoadingGrades(false);
      }
    };

    fetchGrades();
  }, [formik.values.levelId, accessToken]);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Form.Input
        name="name"
        label="Nombre de la clase"
        onChange={formik.handleChange}
        value={formik.values.name}
        error={formik.errors.name}
      />
      <Form.TextArea
        name="description"
        label="Descripción de la clase"
        onChange={formik.handleChange}
        value={formik.values.description}
        error={formik.errors.description}
      />
      <Form.Input
        name="scheduledAt"
        label="Fecha y hora programada"
        type="datetime-local"
        onChange={formik.handleChange}
        value={formik.values.scheduledAt}
        error={formik.errors.scheduledAt}
      />
      <Form.Input
        name="durationMinutes"
        type="number"
        min={1}
        label="Duración en minutos"
        onChange={formik.handleChange}
        value={formik.values.durationMinutes}
        error={formik.errors.durationMinutes}
      />
      <Form.Select
        className="edunova-select"
        name="levelId"
        label="Nivel"
        placeholder="Selecciona un nivel"
        fluid
        clearable
        options={withEmptyOption(levels, "Selecciona un nivel")}
        loading={loadingLevels}
        onChange={(_, data) => {
          formik.setFieldValue("levelId", data.value);
          formik.setFieldValue("gradeId", "");
        }}
        value={formik.values.levelId}
        error={formik.errors.levelId}
      />
      <Form.Select
        className="edunova-select"
        name="gradeId"
        label="Grado"
        placeholder="Selecciona un grado"
        fluid
        clearable
        disabled={!formik.values.levelId}
        options={withEmptyOption(grades, "Selecciona un grado")}
        loading={loadingGrades}
        onChange={(_, data) => formik.setFieldValue("gradeId", data.value)}
        value={formik.values.gradeId}
        error={formik.errors.gradeId}
      />
      <Form.Select
        className="edunova-select"
        name="subjectId"
        label="Asignatura"
        placeholder="Selecciona una asignatura"
        fluid
        clearable
        options={withEmptyOption(subjects, "Selecciona una asignatura")}
        loading={loadingSubjects}
        onChange={(_, data) => formik.setFieldValue("subjectId", data.value)}
        value={formik.values.subjectId}
        error={formik.errors.subjectId}
      />
      <Form.Select
        className="edunova-select"
        name="teacherId"
        label="Docente"
        placeholder="Selecciona un docente"
        fluid
        clearable
        options={withEmptyOption(teachers, "Selecciona un docente")}
        loading={loadingTeachers}
        onChange={(_, data) => formik.setFieldValue("teacherId", data.value)}
        value={formik.values.teacherId}
        error={formik.errors.teacherId}
      />
      <Form.Input
        name="accessCode"
        label="Código de acceso (opcional)"
        onChange={formik.handleChange}
        value={formik.values.accessCode}
        error={formik.errors.accessCode}
      />
      <Button type="submit" primary fluid loading={formik.isSubmitting}>
        {liveClass ? "Actualizar clase en vivo" : "Crear clase en vivo"}
      </Button>
    </Form>
  );
}
